<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import StandaloneTicketModal from './StandaloneTicketModal.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import subDays from 'date-fns/subDays';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { getContactDisplayName } from 'dashboard/helper/contactDisplayName';
import { ticketUpdateErrorMessage } from './ticketErrorMessage';
import { ticketStatusLabel } from './ticketStatusLabel';

export default {
  name: 'TicketsIndex',
  components: {
    Icon,
    ChannelIcon,
    V4Button,
    Avatar,
    StandaloneTicketModal,
    PaginationFooter,
    WootDatePicker,
  },
  data() {
    return {
      viewMode: 'kanban',
      activeFilter: 'all',
      showCreateModal: false,
      searchQuery: '',
      isSidebarOpen: true,
      sidebarWidth: 256,
      isDragging: false,
      startX: 0,
      startWidth: 0,
      showCreateFilterModal: false,
      newFilter: {
        name: '',
        status: '',
        priority: '',
        assignee_id: '',
        team_id: '',
        contact_id: null,
      },
      editingFilterId: null,
      contactQuery: '',
      contactSearchResults: [],
      isSearchingContact: false,
      searchTimeout: null,
      ticketSearchTimeout: null,
      isMobileViewport: false,
      onViewportResize: null,
      selectedContact: null,
      isExporting: false,
      dateRange: [subDays(new Date(), 7), new Date()],
      selectedDateRange: DATE_RANGE_TYPES.LAST_7_DAYS,
    };
  },
  computed: {
    ...mapGetters({
      tickets: 'tickets/getTickets',
      meta: 'tickets/getMeta',
      currentUser: 'getCurrentUser',
      inboxes: 'inboxes/getInboxes',
      customViews: 'customViews/getTicketCustomViews',
      agents: 'agents/getAgents',
      teams: 'teams/getTeams',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    currentAccount() {
      return this.getAccount(this.currentAccountId);
    },
    filterTitle() {
      const titles = {
        my_unresolved: this.$t('TICKETS.FILTERS.MY_UNRESOLVED'),
        unresolved: this.$t('TICKETS.FILTERS.UNRESOLVED'),
        unassigned: this.$t('TICKETS.FILTERS.UNASSIGNED'),
        open: this.$t('TICKETS.FILTERS.OPEN'),
        in_progress: this.$t('TICKETS.FILTERS.IN_PROGRESS'),
        pending: this.$t('TICKETS.FILTERS.PENDING'),
        all: this.$t('TICKETS.FILTERS.ALL'),
        resolved: this.$t('TICKETS.FILTERS.RESOLVED'),
        canceled: this.$t('TICKETS.FILTERS.CANCELED'),
        closed: this.$t('TICKETS.FILTERS.CLOSED'),
        spam: this.$t('TICKETS.FILTERS.SPAM'),
      };
      if (
        typeof this.activeFilter === 'number' ||
        this.activeFilter.startsWith('custom_')
      ) {
        const viewId = parseInt(
          String(this.activeFilter).replace('custom_', ''),
          10
        );
        const view = this.customViews.find(v => v.id === viewId);
        return view ? view.name : 'Filtro Personalizado';
      }
      return titles[this.activeFilter];
    },
    filteredTickets() {
      return this.tickets.filter(ticket => {
        // Busca
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase();
          const titleMatch =
            ticket.title && ticket.title.toLowerCase().includes(q);
          const idMatch = this.displayId(ticket).toLowerCase().includes(q);
          if (!titleMatch && !idMatch) return false;
        }

        // Filtros da Sidebar
        if (this.activeFilter === 'my_unresolved') {
          return (
            ticket.assignee_id === this.currentUser.id &&
            ['open', 'in_progress', 'pending'].includes(ticket.status)
          );
        }
        if (this.activeFilter === 'unresolved') {
          return ['open', 'in_progress', 'pending'].includes(ticket.status);
        }
        if (this.activeFilter === 'unassigned') {
          return !ticket.assignee_id;
        }
        if (this.activeFilter === 'open') {
          return ticket.status === 'open';
        }
        if (this.activeFilter === 'in_progress') {
          return ticket.status === 'in_progress';
        }
        if (this.activeFilter === 'pending') {
          return ticket.status === 'pending';
        }
        if (this.activeFilter === 'resolved') {
          return ticket.status === 'resolved';
        }
        if (this.activeFilter === 'canceled') {
          return ticket.status === 'canceled';
        }
        if (this.activeFilter === 'closed') {
          return ticket.status === 'closed';
        }
        if (this.activeFilter === 'spam') {
          return ticket.status === 'spam';
        }
        if (this.activeFilter === 'all') {
          return true;
        }

        // Custom Filters
        if (
          typeof this.activeFilter === 'number' ||
          String(this.activeFilter).startsWith('custom_')
        ) {
          const viewId = parseInt(
            String(this.activeFilter).replace('custom_', ''),
            10
          );
          const view = this.customViews.find(v => v.id === viewId);
          if (view) {
            return this.evaluateCustomView(ticket, view);
          }
        }
        return true;
      });
    },
    counts() {
      const all = this.tickets || [];
      const localCounts = {
        my_unresolved: all.filter(
          t =>
            t.assignee_id === this.currentUser.id &&
            ['open', 'in_progress', 'pending'].includes(t.status)
        ).length,
        unresolved: all.filter(t =>
          ['open', 'in_progress', 'pending'].includes(t.status)
        ).length,
        unassigned: all.filter(t => !t.assignee_id).length,
        open: all.filter(t => t.status === 'open').length,
        in_progress: all.filter(t => t.status === 'in_progress').length,
        pending: all.filter(t => t.status === 'pending').length,
        resolved: all.filter(t => t.status === 'resolved').length,
        canceled: all.filter(t => t.status === 'canceled').length,
        closed: all.filter(t => t.status === 'closed').length,
        spam: all.filter(t => t.status === 'spam').length,
        all: all.length,
      };
      const baseCounts = {
        ...localCounts,
        ...(this.meta?.counts || {}),
      };

      if (this.customViews && this.customViews.length > 0) {
        this.customViews.forEach(view => {
          baseCounts[`custom_${view.id}`] = all.filter(t =>
            this.evaluateCustomView(t, view)
          ).length;
        });
      }

      return baseCounts;
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this.ticketSearchTimeout);
      this.ticketSearchTimeout = setTimeout(() => this.onPageChange(1), 300);
    },
  },
  mounted() {
    this.onViewportResize = () => {
      this.isMobileViewport = window.innerWidth < 768;
      if (this.isMobileViewport) this.isSidebarOpen = false;
    };
    this.onViewportResize();
    window.addEventListener('resize', this.onViewportResize);

    this.fetchTickets().then(() => {
      if (this.$route && this.$route.query && this.$route.query.ticket_id) {
        const tId = parseInt(this.$route.query.ticket_id, 10);
        const ticket = this.tickets.find(t => t.id === tId);
        if (ticket) {
          this.openTicketDetails(ticket);
        }
      }
    });
    this.$store.dispatch('customViews/get', 'ticket');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');

    // Prevent immediate re-exports if user just clicked it
    const lastExport = localStorage.getItem('ticket_export_last_clicked');
    if (lastExport) {
      const timeSince = Date.now() - parseInt(lastExport, 10);
      if (timeSince < 60000) {
        this.isExporting = true;
        setTimeout(() => {
          this.isExporting = false;
        }, 60000 - timeSince);
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout);
    clearTimeout(this.ticketSearchTimeout);
    this.stopDrag();
    window.removeEventListener('resize', this.onViewportResize);
  },
  methods: {
    contactDisplayName(contact) {
      return getContactDisplayName(contact) || 'Cliente';
    },
    ticketDateRangeParams() {
      if (!this.dateRange || this.dateRange.length !== 2) return {};

      return {
        since: Math.floor(startOfDay(this.dateRange[0]).getTime() / 1000),
        until: Math.floor(endOfDay(this.dateRange[1]).getTime() / 1000),
      };
    },
    onDateRangeChange(value) {
      const [startDate, endDate, rangeType] = value;
      this.dateRange = [startDate, endDate];
      this.selectedDateRange = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
      this.onPageChange(1);
    },
    evaluateCustomView(ticket, view) {
      if (!view || !view.query || !view.query.payload) return true;

      return view.query.payload.every(condition => {
        if (condition.attribute_key === 'status') {
          return ticket.status === condition.values[0];
        }
        if (condition.attribute_key === 'priority') {
          return ticket.priority === condition.values[0];
        }
        if (condition.attribute_key === 'assignee_id') {
          return ticket.assignee_id === parseInt(condition.values[0], 10);
        }
        if (condition.attribute_key === 'team_id') {
          return ticket.team_id === parseInt(condition.values[0], 10);
        }
        return true;
      });
    },
    getInbox(inboxId) {
      if (!this.inboxes) return { channel_type: 'Manual' };
      return (
        this.inboxes.find(inbox => inbox.id === inboxId) || {
          channel_type: 'Manual',
        }
      );
    },
    shouldShowColumn(columnStatus) {
      if (
        this.activeFilter === 'my_unresolved' ||
        this.activeFilter === 'unresolved'
      ) {
        return ['open', 'in_progress', 'pending'].includes(columnStatus);
      }
      if (
        [
          'open',
          'in_progress',
          'pending',
          'resolved',
          'canceled',
          'closed',
          'spam',
        ].includes(this.activeFilter)
      ) {
        return this.activeFilter === columnStatus;
      }
      if (
        typeof this.activeFilter === 'number' ||
        String(this.activeFilter).startsWith('custom_')
      ) {
        const viewId = parseInt(
          String(this.activeFilter).replace('custom_', ''),
          10
        );
        const view = this.customViews.find(v => v.id === viewId);
        if (view && view.query && view.query.payload) {
          const statusCondition = view.query.payload.find(
            c => c.attribute_key === 'status'
          );
          if (
            statusCondition &&
            statusCondition.values &&
            statusCondition.values.length > 0
          ) {
            // Se o filtro selecionou um status específico, só mostra as colunas que estão nesse filtro
            return statusCondition.values.includes(columnStatus);
          }
        }
      }
      return true; // Se não for filtro customizado por status, mostra todas
    },
    urgencyStyle(urgency) {
      if (!urgency) return { backgroundColor: '#f1f5f9', color: '#475569' };

      const config =
        this.currentAccount?.custom_attributes?.ticket_urgencies || [];
      const option = config.find(
        u => u.name && u.name.toLowerCase() === urgency.toLowerCase().trim()
      );

      if (option && option.color) {
        // Return inline style with 15% opacity background (using hex alpha or simple bg/color separation)
        // Here we just return solid text color and 20% opacity bg color
        return {
          backgroundColor: `${option.color}20`,
          color: option.color,
        };
      }

      // Fallback
      const u = urgency.toLowerCase().trim();
      if (['baixa', 'low'].includes(u))
        return { backgroundColor: '#dbeafe', color: '#1d4ed8' };
      if (['média', 'media', 'medium'].includes(u))
        return { backgroundColor: '#fef9c3', color: '#a16207' };
      if (['alta', 'high'].includes(u))
        return { backgroundColor: '#ffedd5', color: '#c2410c' };
      if (['urgente', 'urgent'].includes(u))
        return { backgroundColor: '#fee2e2', color: '#b91c1c' };

      return { backgroundColor: '#f1f5f9', color: '#475569' };
    },
    onContactSearchInput() {
      if (this.contactQuery.trim().length < 3) {
        this.contactSearchResults = [];
        return;
      }
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        this.isSearchingContact = true;
        try {
          const response = await window.axios.get(
            `/api/v1/accounts/${this.currentUser.account_id}/contacts/search?q=${this.contactQuery}`
          );
          this.contactSearchResults = response.data.payload || [];
        } catch {
          useAlert(this.$t('TICKETS.ALERTS.CONTACT_SEARCH_ERROR'));
        } finally {
          this.isSearchingContact = false;
        }
      }, 500);
    },
    selectContact(res) {
      this.selectedContact = res;
      this.newFilter.contact_id = res.id;
      this.contactQuery = '';
      this.contactSearchResults = [];
    },
    removeContact() {
      this.selectedContact = null;
      this.newFilter.contact_id = null;
      this.contactQuery = '';
    },
    channelName(ticket) {
      if (ticket.inbox_id) {
        const inbox = this.getInbox(ticket.inbox_id);
        if (inbox && inbox.name) return inbox.name;
      }
      const type = ticket.channel_type;
      const names = {
        'Channel::Whatsapp': 'WhatsApp',
        'Channel::FacebookPage': 'Messenger',
        'Channel::Instagram': 'Instagram',
        'Channel::Telegram': 'Telegram',
        'Channel::Line': 'Line',
        'Channel::Sms': 'SMS',
        'Channel::TwitterProfile': 'X',
        'Channel::WebWidget': 'Chat',
        'Channel::Api': 'WhatsApp',
        'Channel::Email': 'E-mail',
      };
      return names[type] || 'Avulso';
    },
    channelIcon(type) {
      const icons = {
        'Channel::Whatsapp': 'i-logos-whatsapp-icon',
        'Channel::FacebookPage': 'i-logos-messenger',
        'Channel::Instagram': 'i-woot-instagram-color',
        'Channel::Telegram': 'i-logos-telegram',
        'Channel::Line': 'i-woot-line-color',
        'Channel::Sms': 'i-woot-sms',
        'Channel::TwitterProfile': 'i-woot-x',
        'Channel::WebWidget': 'i-woot-website',
        'Channel::Api': 'i-logos-whatsapp-icon',
        'Channel::Email': 'i-woot-mail',
      };
      return icons[type] || 'i-lucide-ticket';
    },
    async fetchTickets() {
      const payload = {
        page: this.meta.current_page || 1,
        ...this.ticketDateRangeParams(),
      };
      payload.q = this.searchQuery.trim();
      payload.filter = this.activeFilter;
      try {
        return await this.$store.dispatch('tickets/get', payload);
      } catch (error) {
        useAlert(this.$t('TICKETS.ALERTS.LOAD_ERROR'));
        return null;
      }
    },
    async onTicketCreated() {
      this.showCreateModal = false;
      await this.fetchTickets();
    },
    async onPageChange(page) {
      const payload = { page, ...this.ticketDateRangeParams() };
      payload.q = this.searchQuery.trim();
      payload.filter = this.activeFilter;
      try {
        await this.$store.dispatch('tickets/get', payload);
      } catch (error) {
        useAlert(this.$t('TICKETS.ALERTS.PAGE_LOAD_ERROR'));
      }
    },
    async exportTickets() {
      if (this.isExporting) return;
      this.isExporting = true;
      localStorage.setItem('ticket_export_last_clicked', Date.now().toString());

      const payload = this.ticketDateRangeParams();

      if (this.activeFilter && this.activeFilter !== 'all') {
        payload.filter = this.activeFilter;
      }

      try {
        await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/tickets/export`,
          payload
        );
        useAlert(this.$t('TICKETS.ALERTS.EXPORT_STARTED'));
      } catch (e) {
        useAlert(this.$t('TICKETS.ALERTS.EXPORT_ERROR'));
        this.isExporting = false;
        localStorage.removeItem('ticket_export_last_clicked');
      }

      // Re-enable after 60 seconds
      setTimeout(() => {
        this.isExporting = false;
      }, 60000);
    },
    setFilter(filterName) {
      this.activeFilter = filterName;
      this.onPageChange(1);
    },
    startDrag(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startWidth = this.sidebarWidth;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      document.body.style.userSelect = 'none';
    },
    onDrag(e) {
      if (!this.isDragging) return;
      let newWidth = this.startWidth + (e.clientX - this.startX);
      if (newWidth < 200) newWidth = 200;
      if (newWidth > 600) newWidth = 600;
      this.sidebarWidth = newWidth;
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.body.style.userSelect = '';
    },
    openTicketDetails(ticket) {
      this.$router.push({
        name: 'tickets_dashboard_details',
        params: {
          accountId: this.$route.params.accountId,
          ticketId: ticket.id,
        },
      });
    },
    ticketsByStatus(status) {
      return this.filteredTickets.filter(t => t.status === status);
    },
    displayId(ticket) {
      const paddedId = String(ticket.id).padStart(6, '0');
      return ticket.conversation_id ? `TC-${paddedId}` : `TA-${paddedId}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    },
    statusLabel(status) {
      // Only the closed status map in ticketStatusLabel.js can reach this lookup.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return ticketStatusLabel(status, key => this.$t(key));
    },
    statusColor(status) {
      const colors = {
        open: 'bg-slate-100 text-slate-800 dark:bg-slate-700/50 dark:text-slate-300',
        in_progress:
          'bg-woot-100 text-woot-800 dark:bg-woot-900/50 dark:text-woot-300',
        pending:
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
        resolved:
          'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
        canceled:
          'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
        closed:
          'bg-slate-100 text-slate-800 dark:bg-slate-700/50 dark:text-slate-300',
        spam: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
      };
      return colors[status] || 'bg-slate-100 text-slate-800';
    },
    urgencyColor(urgency) {
      const lower = (urgency || '').toLowerCase();
      if (
        ['alta', 'high', 'urgente', 'urgent'].some(value =>
          lower.includes(value)
        )
      )
        return 'border-red-400';
      if (['média', 'media', 'medium'].some(value => lower.includes(value)))
        return 'border-yellow-400';
      return 'border-woot-400';
    },
    urgencyBgColor(urgency) {
      const lower = (urgency || '').toLowerCase();
      if (
        ['alta', 'high', 'urgente', 'urgent'].some(value =>
          lower.includes(value)
        )
      )
        return 'bg-red-500';
      if (['média', 'media', 'medium'].some(value => lower.includes(value)))
        return 'bg-yellow-500';
      if (['baixa', 'low'].some(value => lower.includes(value)))
        return 'bg-blue-500';
      return 'bg-slate-400';
    },
    urgencyLabel(urgency) {
      const normalized = (urgency || '').trim().toLowerCase();
      const translationKeys = {
        baixa: 'TICKET_DETAILS.URGENCY_LOW',
        low: 'TICKET_DETAILS.URGENCY_LOW',
        média: 'TICKET_DETAILS.URGENCY_MEDIUM',
        media: 'TICKET_DETAILS.URGENCY_MEDIUM',
        medium: 'TICKET_DETAILS.URGENCY_MEDIUM',
        alta: 'TICKET_DETAILS.URGENCY_HIGH',
        high: 'TICKET_DETAILS.URGENCY_HIGH',
        urgente: 'TICKET_DETAILS.URGENCY_URGENT',
        urgent: 'TICKET_DETAILS.URGENCY_URGENT',
      };
      const key = translationKeys[normalized];
      // key is resolved only from the fixed urgency translation map above.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return key ? this.$t(key) : urgency;
    },
    onDragStart(e, ticket) {
      e.dataTransfer.setData('ticketId', ticket.id);
      e.dataTransfer.effectAllowed = 'move';
    },
    async onDrop(e, newStatus) {
      const ticketId = e.dataTransfer.getData('ticketId');
      if (!ticketId) return;

      const ticket = this.tickets.find(t => String(t.id) === String(ticketId));
      if (!ticket || ticket.status === newStatus) return;

      try {
        await this.$store.dispatch('tickets/update', {
          id: Number(ticketId),
          status: newStatus,
        });
        useAlert(this.$t('TICKETS.ALERTS.STATUS_UPDATED'));
        await this.fetchTickets();
      } catch (error) {
        useAlert(ticketUpdateErrorMessage(error, this.$t));
      }
    },
    async createCustomFilter() {
      if (!this.newFilter.name) {
        useAlert(this.$t('TICKETS.ALERTS.FILTER_NAME_REQUIRED'));
        return;
      }

      const payload = [];
      if (this.newFilter.status) {
        payload.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: [this.newFilter.status],
          query_operator: 'and',
        });
      }

      if (this.newFilter.assignee_id) {
        payload.push({
          attribute_key: 'assignee_id',
          filter_operator: 'equal_to',
          values: [this.newFilter.assignee_id],
          query_operator: 'and',
        });
      }

      if (this.newFilter.team_id) {
        payload.push({
          attribute_key: 'team_id',
          filter_operator: 'equal_to',
          values: [this.newFilter.team_id],
          query_operator: 'and',
        });
      }

      if (this.newFilter.priority) {
        payload.push({
          attribute_key: 'priority',
          filter_operator: 'equal_to',
          values: [this.newFilter.priority],
          query_operator: 'and',
        });
      }

      if (this.newFilter.contact_id) {
        payload.push({
          attribute_key: 'contact_id',
          filter_operator: 'equal_to',
          values: [this.newFilter.contact_id],
          query_operator: 'and',
        });
      }

      if (this.editingFilterId) {
        try {
          await this.$store.dispatch('customViews/update', {
            id: this.editingFilterId,
            name: this.newFilter.name,
            filter_type: 3, // TICKET
            query: {
              payload,
            },
          });
          useAlert(this.$t('TICKETS.ALERTS.FILTER_UPDATED'));
          this.showCreateFilterModal = false;
          this.resetFilterForm();
          this.$store.dispatch('customViews/get', 'ticket');
        } catch (error) {
          useAlert(this.$t('TICKETS.ALERTS.FILTER_UPDATE_ERROR'));
        }
      } else {
        try {
          await this.$store.dispatch('customViews/create', {
            name: this.newFilter.name,
            filter_type: 3, // TICKET
            query: {
              payload,
            },
          });
          useAlert(this.$t('TICKETS.ALERTS.FILTER_CREATED'));
          this.showCreateFilterModal = false;
          this.resetFilterForm();
          this.$store.dispatch('customViews/get', 'ticket');
        } catch (error) {
          useAlert(this.$t('TICKETS.ALERTS.FILTER_CREATE_ERROR'));
        }
      }
    },
    resetFilterForm() {
      this.editingFilterId = null;
      this.newFilter = {
        name: '',
        status: '',
        priority: '',
        assignee_id: '',
        team_id: '',
        contact_id: null,
      };
      this.selectedContact = null;
    },
    openCreateFilterModal() {
      this.resetFilterForm();
      this.showCreateFilterModal = true;
    },
    async openEditFilter(view) {
      this.resetFilterForm();
      this.editingFilterId = view.id;
      this.newFilter.name = view.name;

      if (view.query && view.query.payload) {
        view.query.payload.forEach(condition => {
          if (condition.attribute_key === 'status')
            this.newFilter.status = condition.values[0];
          if (condition.attribute_key === 'priority')
            this.newFilter.priority = condition.values[0];
          if (condition.attribute_key === 'assignee_id')
            this.newFilter.assignee_id = condition.values[0];
          if (condition.attribute_key === 'team_id')
            this.newFilter.team_id = condition.values[0];
          if (condition.attribute_key === 'contact_id') {
            this.newFilter.contact_id = condition.values[0];
            // Fetch contact to show in UI
            if (this.newFilter.contact_id) {
              window.axios
                .get(
                  `/api/v1/accounts/${this.currentUser.account_id}/contacts/${this.newFilter.contact_id}`
                )
                .then(res => {
                  this.selectedContact = res.data.payload;
                })
                .catch(() => {});
            }
          }
        });
      }
      this.showCreateFilterModal = true;
    },
    async deleteFilter(view) {
      const ok = await this.$refs.confirmDialog.showConfirmation();
      if (ok) {
        try {
          await this.$store.dispatch('customViews/delete', {
            id: view.id,
            filterType: 'ticket',
          });
          useAlert(this.$t('TICKETS.ALERTS.FILTER_DELETED'));
          if (
            this.activeFilter === `custom_${view.id}` ||
            this.activeFilter === view.id
          ) {
            this.setFilter('all');
          }
        } catch (e) {
          useAlert(this.$t('TICKETS.ALERTS.FILTER_DELETE_ERROR'));
        }
      }
    },
  },
};
</script>

<template>
  <div class="flex h-full w-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
    <!-- Left Sidebar (Filtros Rápidos) -->
    <div
      v-show="isSidebarOpen"
      class="bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-none"
      :class="
        isMobileViewport
          ? 'fixed inset-y-0 left-0 z-40 w-[min(85vw,20rem)] shadow-xl'
          : 'relative flex-shrink-0'
      "
      :style="isMobileViewport ? undefined : { width: sidebarWidth + 'px' }"
    >
      <aside class="w-full h-full overflow-y-auto">
        <div class="p-4 flex flex-col gap-1">
          <div
            class="flex items-center justify-between mb-2 px-2 pb-2 border-b border-slate-200 dark:border-slate-700"
          >
            <h2
              class="text-xs font-semibold text-slate-500 dark:text-slate-400"
            >
              {{ $t('COMMON.TICKETS_PANEL') }}
            </h2>
            <button
              class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              :title="$t('COMMON.COLLAPSE_PANEL')"
              @click="isSidebarOpen = false"
            >
              <Icon icon="i-lucide-chevron-left" class="size-4" />
            </button>
          </div>
          <div class="flex flex-col space-y-1">
            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'my_unresolved'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('my_unresolved')"
            >
              <Icon icon="i-lucide-user" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.MY_UNRESOLVED_TICKETS')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.my_unresolved }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'unresolved'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('unresolved')"
            >
              <Icon icon="i-lucide-users" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.ALL_UNRESOLVED_TICKETS')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.unresolved }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'unassigned'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('unassigned')"
            >
              <Icon icon="i-lucide-user-minus" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.UNASSIGNED_TICKETS')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.unassigned }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'open'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('open')"
            >
              <Icon icon="i-lucide-circle" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.OPEN')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.open }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'in_progress'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('in_progress')"
            >
              <Icon icon="i-lucide-play-circle" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.IN_PROGRESS')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.in_progress }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'pending'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('pending')"
            >
              <Icon icon="i-lucide-clock" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.PENDING')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.pending }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'resolved'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('resolved')"
            >
              <Icon icon="i-lucide-check-circle" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.RESOLVED_TICKETS')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.resolved }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'closed'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('closed')"
            >
              <Icon icon="i-lucide-lock" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.CLOSED')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.closed }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'canceled'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('canceled')"
            >
              <Icon icon="i-lucide-x-circle" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.CANCELED')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.canceled }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'spam'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('spam')"
            >
              <Icon icon="i-lucide-alert-octagon" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.SPAM')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.spam }}</span
              >
            </button>

            <button
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="[
                activeFilter === 'all'
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter('all')"
            >
              <Icon icon="i-lucide-inbox" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{
                $t('COMMON.ALL_TICKETS')
              }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50"
                >{{ counts.all }}</span
              >
            </button>
          </div>

          <div class="mt-6 flex flex-col gap-1">
            <div
              class="flex items-center justify-between mb-2 px-2 pb-2 border-b border-slate-200 dark:border-slate-700"
            >
              <h2
                class="text-xs font-semibold text-slate-500 dark:text-slate-400"
              >
                {{ $t('COMMON.CUSTOM_FILTERS') }}
              </h2>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                :title="$t('COMMON.NEW_FILTER')"
                @click="openCreateFilterModal"
              >
                <Icon icon="i-lucide-plus" class="size-4" />
              </button>
            </div>
            <div
              v-for="view in customViews"
              :key="view.id"
              role="button"
              tabindex="0"
              class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors group relative"
              :class="[
                activeFilter === `custom_${view.id}` || activeFilter === view.id
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 border border-transparent',
              ]"
              @click="setFilter(`custom_${view.id}`)"
              @keydown.enter="setFilter(`custom_${view.id}`)"
              @keydown.space.prevent="setFilter(`custom_${view.id}`)"
            >
              <Icon icon="i-lucide-filter" class="size-4 shrink-0" />
              <span class="truncate flex-1 text-left">{{ view.name }}</span>
              <span
                class="text-xs py-0.5 px-2 rounded-full bg-slate-200/50 dark:bg-slate-700/50 mr-1 group-hover:hidden"
                >{{ counts[`custom_${view.id}`] }}</span
              >
              <div class="hidden group-hover:flex items-center gap-1">
                <button
                  type="button"
                  class="p-1 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors"
                  :title="$t('COMMON.EDIT_FILTER')"
                  @click.stop="openEditFilter(view)"
                >
                  <Icon icon="i-lucide-pencil" class="size-3.5" />
                </button>
                <button
                  type="button"
                  class="p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                  :title="$t('COMMON.DELETE_FILTER')"
                  @click.stop="deleteFilter(view)"
                >
                  <Icon icon="i-lucide-trash-2" class="size-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- Resizer Handle -->
      <div
        v-show="!isMobileViewport"
        class="absolute top-0 right-[-3px] w-1.5 h-full cursor-col-resize hover:bg-blue-400 z-10 transition-colors"
        @mousedown="startDrag"
      />

      <woot-confirm-modal
        ref="confirmDialog"
        :title="$t('COMMON.DELETE_FILTER_MENU')"
        :description="$t('COMMON.DELETE_FILTER_CONFIRM')"
        :confirm-label="$t('COMMON.YES_DELETE')"
        :cancel-label="$t('COMMON.CANCEL')"
      />
    </div>

    <!-- Main Content -->
    <main class="flex min-w-0 flex-1 flex-col overflow-hidden">
      <!-- Header Area -->
      <div class="shrink-0 px-4 py-4 sm:px-6">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-4 flex-1">
            <button
              v-show="isMobileViewport || !isSidebarOpen"
              class="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              :title="$t('COMMON.EXPAND_PANEL')"
              @click="isSidebarOpen = true"
            >
              <Icon icon="i-lucide-menu" class="size-5" />
            </button>
            <h1
              class="text-xl font-bold text-slate-800 dark:text-slate-100 tracking-tight"
            >
              {{ filterTitle }}
            </h1>
            <span
              class="text-sm text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md"
            >
              {{ $t('COMMON.RECORDS_COUNT', { count: meta.total_count || 0 }) }}
            </span>
          </div>

          <div class="flex flex-wrap items-center gap-2 sm:gap-3">
            <WootDatePicker
              v-model:date-range="dateRange"
              v-model:range-type="selectedDateRange"
              position="right"
              disable-future-dates
              @date-range-changed="onDateRangeChange"
            />
            <V4Button
              color="slate"
              variant="outline"
              icon="i-lucide-download"
              :label="$t('COMMON.EXPORT')"
              :is-loading="isExporting"
              @click="exportTickets"
            />
            <V4Button
              color="slate"
              variant="ghost"
              icon="i-lucide-refresh-cw"
              :aria-label="$t('COMMON.REFRESH_TICKETS')"
              @click="fetchTickets"
            />
            <V4Button
              color="blue"
              icon="i-lucide-plus"
              :label="$t('COMMON.NEW_TICKET')"
              @click="showCreateModal = true"
            />
          </div>
        </div>

        <!-- Create Filter Modal -->
        <div
          v-if="showCreateFilterModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
        >
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden"
          >
            <div
              class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center"
            >
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{
                  editingFilterId
                    ? $t('COMMON.EDIT_FILTER_MENU')
                    : $t('COMMON.CREATE_FILTER_MENU')
                }}
              </h3>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                @click="showCreateFilterModal = false"
              >
                <Icon icon="i-lucide-x" class="size-5" />
              </button>
            </div>
            <div class="p-6 space-y-4">
              <div>
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >{{ $t('COMMON.MENU_NAME') }}</label
                >
                <input
                  v-model="newFilter.name"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                  :placeholder="$t('COMMON.FILTER_NAME_PLACEHOLDER')"
                />
              </div>

              <!-- Solicitante Search -->
              <div class="relative">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >{{ $t('COMMON.REQUESTER_OPTIONAL') }}</label
                >
                <div
                  v-if="selectedContact"
                  class="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 relative"
                >
                  <Avatar
                    :src="selectedContact.thumbnail"
                    :name="selectedContact.name"
                    :size="32"
                    rounded-full
                  />
                  <div class="min-w-0 flex-1">
                    <p
                      class="text-sm font-medium text-slate-900 dark:text-white truncate"
                    >
                      {{ selectedContact.name }}
                    </p>
                    <p class="text-xs text-slate-500 truncate">
                      {{ selectedContact.email }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
                    :title="$t('COMMON.REMOVE_CONTACT')"
                    @click="removeContact"
                  >
                    <Icon icon="i-lucide-x" class="size-4" />
                  </button>
                </div>
                <div v-else class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Icon
                      icon="i-lucide-search"
                      class="size-4 text-slate-400"
                    />
                  </div>
                  <input
                    v-model="contactQuery"
                    type="text"
                    class="w-full !pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                    :placeholder="$t('COMMON.SEARCH_CONTACT_PLACEHOLDER')"
                    @input="onContactSearchInput"
                  />
                  <div
                    v-if="contactQuery.length >= 3"
                    class="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  >
                    <div
                      v-if="isSearchingContact"
                      class="p-3 text-sm text-slate-500 text-center"
                    >
                      {{ $t('COMMON.SEARCHING') }}
                    </div>
                    <div
                      v-else-if="contactSearchResults.length === 0"
                      class="p-3 text-sm text-slate-500 text-center"
                    >
                      {{ $t('COMMON.NO_CONTACT_FOUND') }}
                    </div>
                    <ul v-else class="py-1">
                      <li
                        v-for="res in contactSearchResults"
                        :key="res.id"
                        class="px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-3"
                        @click="selectContact(res)"
                      >
                        <Avatar
                          :src="res.thumbnail"
                          :name="res.name"
                          :size="24"
                          rounded-full
                        />
                        <div class="min-w-0">
                          <p
                            class="text-sm font-medium text-slate-900 dark:text-white truncate"
                          >
                            {{ res.name }}
                          </p>
                          <p class="text-xs text-slate-500 truncate">
                            {{ res.email }}
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >{{ $t('COMMON.STATUS') }}</label
                  >
                  <select
                    v-model="newFilter.status"
                    class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="">{{ $t('COMMON.ALL') }}</option>
                    <option value="open">{{ $t('COMMON.OPEN') }}</option>
                    <option value="in_progress">
                      {{ $t('COMMON.IN_PROGRESS') }}
                    </option>
                    <option value="pending">{{ $t('COMMON.PENDING') }}</option>
                    <option value="resolved">
                      {{ $t('COMMON.RESOLVED') }}
                    </option>
                    <option value="canceled">
                      {{ $t('COMMON.CANCELED') }}
                    </option>
                  </select>
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >{{ $t('COMMON.ASSIGNEE') }}</label
                  >
                  <select
                    v-model="newFilter.assignee_id"
                    class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="">{{ $t('COMMON.ALL') }}</option>
                    <option
                      v-for="agent in agents"
                      :key="agent.id"
                      :value="agent.id"
                    >
                      {{ agent.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >{{ $t('COMMON.PRIORITY') }}</label
                  >
                  <select
                    v-model="newFilter.priority"
                    class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="">{{ $t('COMMON.ALL_FEMININE') }}</option>
                    <option value="low">{{ $t('COMMON.LOW') }}</option>
                    <option value="medium">{{ $t('COMMON.MEDIUM') }}</option>
                    <option value="high">{{ $t('COMMON.HIGH') }}</option>
                    <option value="urgent">{{ $t('COMMON.URGENT') }}</option>
                  </select>
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >{{ $t('COMMON.TEAM_GROUP') }}</label
                  >
                  <select
                    v-model="newFilter.team_id"
                    class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="">{{ $t('COMMON.ALL') }}</option>
                    <option
                      v-for="team in teams"
                      :key="team.id"
                      :value="team.id"
                    >
                      {{ team.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div
              class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3"
            >
              <button
                class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                @click="showCreateFilterModal = false"
              >
                {{ $t('COMMON.CANCEL') }}
              </button>
              <button
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                @click="createCustomFilter"
              >
                {{ $t('COMMON.SAVE_MENU') }}
              </button>
            </div>
          </div>
        </div>

        <div
          class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
        >
          <div class="relative w-full shrink-0 sm:w-64">
            <span
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400"
            >
              <Icon icon="i-lucide-search" class="size-4" />
            </span>
            <input
              v-model="searchQuery"
              type="text"
              :aria-label="$t('COMMON.SEARCH_TICKETS_ARIA')"
              class="w-full !pl-9 !pr-3 !py-1.5 !mb-0 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-slate-100 transition-colors"
              :placeholder="$t('COMMON.SEARCH')"
            />
          </div>

          <div class="hidden flex-1 sm:block" />

          <div
            class="flex items-center bg-slate-100 dark:bg-slate-700 p-1 rounded-lg"
          >
            <V4Button
              :color="viewMode === 'kanban' ? 'slate' : 'slate'"
              :variant="viewMode === 'kanban' ? 'solid' : 'ghost'"
              icon="i-lucide-layout-grid"
              size="sm"
              :aria-label="$t('COMMON.KANBAN_VIEW')"
              :aria-pressed="viewMode === 'kanban'"
              @click="viewMode = 'kanban'"
            />
            <V4Button
              :color="viewMode === 'list' ? 'slate' : 'slate'"
              :variant="viewMode === 'list' ? 'solid' : 'ghost'"
              icon="i-lucide-list"
              size="sm"
              :aria-label="$t('COMMON.LIST_VIEW')"
              :aria-pressed="viewMode === 'list'"
              @click="viewMode = 'list'"
            />
          </div>
        </div>
      </div>

      <!-- Content Area -->
      <div
        class="flex-1 overflow-auto bg-slate-50 p-3 dark:bg-slate-900 sm:p-6"
      >
        <!-- List View -->
        <div
          v-if="viewMode === 'list'"
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden"
        >
          <div class="overflow-x-auto">
            <table class="min-w-[720px] w-full text-left border-collapse">
              <thead>
                <tr
                  class="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                >
                  <th class="px-6 py-4">{{ $t('COMMON.ID') }}</th>
                  <th class="px-6 py-4">{{ $t('COMMON.SUBJECT') }}</th>
                  <th class="px-6 py-4">{{ $t('COMMON.STATUS') }}</th>
                  <th class="px-6 py-4">{{ $t('COMMON.PRIORITY') }}</th>
                  <th class="px-6 py-4">{{ $t('COMMON.ASSIGNEE') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
                <tr
                  v-for="ticket in filteredTickets"
                  :key="ticket.id"
                  role="button"
                  tabindex="0"
                  :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                  class="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group"
                  @click="openTicketDetails(ticket)"
                  @keydown.enter="openTicketDetails(ticket)"
                  @keydown.space.prevent="openTicketDetails(ticket)"
                >
                  <td
                    class="px-6 py-4 text-sm font-bold text-blue-600 dark:text-blue-400"
                  >
                    {{ displayId(ticket) }}
                  </td>
                  <td class="px-6 py-4">
                    <p
                      class="text-sm font-semibold text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                    >
                      {{ ticket.title }}
                    </p>
                    <div
                      class="flex items-center gap-1 mt-1 text-slate-500 dark:text-slate-400"
                    >
                      <ChannelIcon
                        v-if="ticket.inbox_id"
                        :inbox="getInbox(ticket.inbox_id)"
                        use-brand-icon
                        class="size-3.5"
                      />
                      <Icon
                        v-else
                        :icon="channelIcon(ticket.channel_type)"
                        class="size-3.5"
                      />
                      <span class="text-xs line-clamp-1"
                        >{{ channelName(ticket) }}
                        {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                        {{ contactDisplayName(ticket.contact) }}</span
                      >
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="[statusColor(ticket.status)]"
                    >
                      {{ statusLabel(ticket.status) }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <span
                      v-if="ticket.urgency"
                      class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <div
                        class="w-2 h-2 rounded-full"
                        :class="urgencyBgColor(ticket.urgency)"
                      />
                      {{ urgencyLabel(ticket.urgency) }}
                    </span>
                    <span
                      v-else
                      class="text-sm text-slate-400 dark:text-slate-500"
                      >{{ $t('COMMON.NOT_AVAILABLE') }}</span
                    >
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-2">
                      <Avatar
                        v-if="ticket.assignee"
                        :src="ticket.assignee.thumbnail"
                        :name="ticket.assignee.name"
                        :size="24"
                        rounded-full
                      />
                      <div
                        v-else
                        class="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300"
                        text-content="?"
                      />
                      <span
                        class="text-sm text-slate-700 dark:text-slate-300"
                        >{{
                          ticket.assignee
                            ? ticket.assignee.name
                            : $t('COMMON.UNASSIGNED')
                        }}</span
                      >
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <PaginationFooter
            v-if="meta && meta.total_count"
            :current-page="Number(meta.current_page)"
            :total-items="meta.total_count"
            :items-per-page="25"
            class="mt-4 px-4 pb-4"
            @update:current-page="onPageChange"
          />
        </div>

        <!-- Kanban View -->
        <div
          v-else-if="viewMode === 'kanban'"
          class="flex gap-6 h-full min-h-0 overflow-x-auto pb-4"
        >
          <!-- Coluna A Fazer -->
          <div
            v-if="shouldShowColumn('open')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-slate-700 dark:bg-slate-900 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.OPEN') }}</span>
              <span class="bg-slate-600 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('open').length
              }}</span>
            </div>
            <div
              class="p-3 flex-1 overflow-y-auto space-y-3"
              :aria-label="$t('COMMON.TICKETS_DROP_OPEN')"
              @dragover.prevent
              @drop="onDrop($event, 'open')"
            >
              <div
                v-for="ticket in ticketsByStatus('open')"
                :key="ticket.id"
                draggable="true"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-slate-500 hover:shadow-md cursor-pointer transition-shadow"
                @dragstart="onDragStart($event, ticket)"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <div
                      v-else
                      class="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700"
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.NO_ASSIGNEE')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Coluna Fazendo -->
          <div
            v-if="shouldShowColumn('in_progress')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-woot-600 dark:bg-woot-800 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.IN_PROGRESS') }}</span>
              <span class="bg-woot-500 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('in_progress').length
              }}</span>
            </div>
            <div
              class="p-3 flex-1 overflow-y-auto space-y-3"
              @dragover.prevent
              @drop="onDrop($event, 'in_progress')"
            >
              <div
                v-for="ticket in ticketsByStatus('in_progress')"
                :key="ticket.id"
                draggable="true"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-woot-500 hover:shadow-md cursor-pointer transition-shadow"
                @dragstart="onDragStart($event, ticket)"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.UNASSIGNED')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Coluna Pendente -->
          <div
            v-if="shouldShowColumn('pending')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-yellow-600 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.PENDING') }}</span>
              <span class="bg-yellow-500 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('pending').length
              }}</span>
            </div>
            <div
              class="p-3 flex-1 overflow-y-auto space-y-3"
              @dragover.prevent
              @drop="onDrop($event, 'pending')"
            >
              <div
                v-for="ticket in ticketsByStatus('pending')"
                :key="ticket.id"
                draggable="true"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-yellow-500 hover:shadow-md cursor-pointer transition-shadow"
                @dragstart="onDragStart($event, ticket)"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.SYSTEM')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Coluna Concluído -->
          <div
            v-if="shouldShowColumn('resolved')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-green-600 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.RESOLVED') }}</span>
              <span class="bg-green-600 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('resolved').length
              }}</span>
            </div>
            <div
              class="p-3 flex-1 overflow-y-auto space-y-3"
              @dragover.prevent
              @drop="onDrop($event, 'resolved')"
            >
              <div
                v-for="ticket in ticketsByStatus('resolved')"
                :key="ticket.id"
                draggable="true"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-green-500 hover:shadow-md cursor-pointer transition-shadow opacity-75"
                @dragstart="onDragStart($event, ticket)"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500 line-through">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.SYSTEM')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- Coluna Fechado -->
          <div
            v-if="shouldShowColumn('closed')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-slate-600 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.CLOSED') }}</span>
              <span class="bg-slate-500 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('closed').length
              }}</span>
            </div>
            <div class="p-3 flex-1 overflow-y-auto space-y-3">
              <div
                v-for="ticket in ticketsByStatus('closed')"
                :key="ticket.id"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-slate-500 hover:shadow-md cursor-pointer transition-shadow opacity-75"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500 line-through">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2 text-slate-500 line-through"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.SYSTEM')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- Coluna Cancelado -->
          <div
            v-if="shouldShowColumn('canceled')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-red-600 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.CANCELED') }}</span>
              <span class="bg-red-600 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('canceled').length
              }}</span>
            </div>
            <div class="p-3 flex-1 overflow-y-auto space-y-3">
              <div
                v-for="ticket in ticketsByStatus('canceled')"
                :key="ticket.id"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-red-500 hover:shadow-md cursor-pointer transition-shadow opacity-75"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500 line-through">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2 text-slate-500 line-through"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.SYSTEM')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- Coluna Spam -->
          <div
            v-if="shouldShowColumn('spam')"
            class="flex flex-col w-80 shrink-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-slate-800 text-white px-4 py-2 font-medium flex justify-between items-center text-sm"
            >
              <span>{{ $t('COMMON.SPAM') }}</span>
              <span class="bg-slate-700 px-2 py-0.5 rounded-full text-xs">{{
                ticketsByStatus('spam').length
              }}</span>
            </div>
            <div class="p-3 flex-1 overflow-y-auto space-y-3">
              <div
                v-for="ticket in ticketsByStatus('spam')"
                :key="ticket.id"
                role="button"
                tabindex="0"
                :aria-label="`Abrir ${displayId(ticket)}: ${ticket.title}`"
                class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 border-l-4 border-slate-900 hover:shadow-md cursor-pointer transition-shadow opacity-75"
                @click="openTicketDetails(ticket)"
                @keydown.enter="openTicketDetails(ticket)"
                @keydown.space.prevent="openTicketDetails(ticket)"
              >
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-bold text-slate-500 line-through">{{
                    displayId(ticket)
                  }}</span>
                  <span class="text-[10px] text-slate-400">{{
                    formatDate(ticket.created_at)
                  }}</span>
                </div>
                <h4
                  class="text-sm font-semibold text-slate-900 dark:text-white mb-2 leading-tight line-clamp-2 text-slate-500 line-through"
                >
                  {{ ticket.title }}
                </h4>
                <div
                  class="flex items-center gap-1 mb-3 text-slate-500 dark:text-slate-400"
                >
                  <ChannelIcon
                    v-if="ticket.inbox_id"
                    :inbox="getInbox(ticket.inbox_id)"
                    use-brand-icon
                    class="size-3"
                  />
                  <Icon
                    v-else
                    :icon="channelIcon(ticket.channel_type)"
                    class="size-3"
                  />
                  <span class="text-xs line-clamp-1"
                    >{{ channelName(ticket) }}
                    {{ $t('COMMON.TICKET_CHANNEL_SEPARATOR') }}
                    {{ contactDisplayName(ticket.contact) }}</span
                  >
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="ticket.assignee"
                      :src="ticket.assignee.thumbnail"
                      :name="ticket.assignee.name"
                      :size="20"
                      rounded-full
                    />
                    <span
                      class="text-xs text-slate-600 dark:text-slate-400 truncate"
                      >{{
                        ticket.assignee
                          ? ticket.assignee.name
                          : $t('COMMON.SYSTEM')
                      }}</span
                    >
                  </div>
                  <span
                    v-if="ticket.urgency"
                    class="text-[10px] px-2 py-0.5 rounded-md font-medium truncate max-w-[80px]"
                    :style="urgencyStyle(ticket.urgency)"
                    >{{ urgencyLabel(ticket.urgency) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Modal de Criação Avulsa -->
    <woot-modal v-model:show="showCreateModal" @close="showCreateModal = false">
      <StandaloneTicketModal
        :account-id="currentAccountId"
        @close="showCreateModal = false"
        @created="onTicketCreated"
      />
    </woot-modal>
  </div>
</template>
